import React from 'react';
import { motion } from 'framer-motion';
import { SmartphoneIcon, FlowerIcon, CheckIcon, ClockIcon } from 'lucide-react';
import { CTAButton } from './CTAButton';
import { OfferCountdownInline } from './OfferCountdownInline';
import { AppCarousel } from './AppCarousel';
import { Divider } from './Divider';
const heroBenefits = ['Vuelve la mujer que eras', 'Recupera tu brillo natural', 'Siente tu cuerpo diferente', 'Duerme sin preocupaciones', 'Come sin culpa', 'Vive con energía real'];
const heroHighlights = [{
  icon: SmartphoneIcon,
  label: 'Nuevas tareas cada día'
}, {
  icon: ClockIcon,
  label: 'Pensado para tu vida real'
}, {
  icon: FlowerIcon,
  label: '30 días. 30 pequeños cambios'
}];
type HeroProps = {
  onCTA: () => void;
};
export function Hero({
  onCTA
}: HeroProps) {
  return <section id="inicio" className="relative w-full overflow-hidden bg-crema pt-28 pb-16 sm:pt-32 sm:pb-24" data-id="element-89" data-mp="3czfkty10i55i">
      <div className="mx-auto grid max-w-6xl items-start gap-12 px-4 sm:px-6 lg:grid-cols-2 lg:gap-10" data-id="element-90" data-mp="3j8ivmv0mo8sl">
        <motion.div initial={{
        opacity: 0,
        y: 24
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.7,
        ease: [0.22, 1, 0.36, 1]
      }} className="mx-auto max-w-md text-center lg:mx-0 lg:text-left" data-id="element-91" data-mp="3o7bzrwu0yng3">
          <p className="font-serif text-4xl font-semibold leading-[1.1] text-tinta sm:text-5xl lg:text-6xl" data-id="element-92" data-mp="30gq6jbooj7q5">
            Recupera tu bienestar en 30 días,
            <span className="block text-rosa-dark" data-id="element-93" data-mp="2qkmrj58qbels">
              con pequeños cambios diarios desde tu celular.
            </span>
          </p>
          <p className="mt-3 text-base leading-relaxed text-tinta/75" data-id="element-94" data-mp="2vj7sx7zpw4bt">
            <strong className="font-semibold text-tinta" data-id="element-95" data-mp="2hkgibvk2d0sf">
              Una app guiada
            </strong>{' '}
            para mujeres que quieren sentirse más livianas, con más energía y en
            equilibrio,{' '}
            <strong className="font-semibold text-tinta" data-id="element-96" data-mp="s3cmgcr3vnrm">
              sin dietas extremas
            </strong>{' '}
            ni rutinas imposibles.
          </p>
          <ul className="mt-5 flex flex-col gap-2.5" data-id="element-97" data-mp="id9u8ert6duc">
            {heroHighlights.map(item => <li key={item.label} className="glow-pulse-border flex items-center gap-3 rounded-2xl border border-nude/70 bg-white/70 px-4 py-3 text-left text-sm font-medium text-tinta shadow-sm sm:text-base" data-id="element-98" data-mp="de6p6ubtw7md">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-rosa/15 text-rosa-dark" aria-hidden="true" data-id="element-99" data-mp="112px38qyuidx">
                  <item.icon className="h-5 w-5" strokeWidth={1.8} data-id="element-100" data-mp="x3h6gcr830ml" />
                </span>
                {item.label}
              </li>)}
          </ul>
          <div className="mt-6 flex justify-center lg:justify-start" data-id="element-101" data-mp="3hpsff7k1zv33">
            <CTAButton onClick={onCTA} icon={SmartphoneIcon} data-id="element-102" data-mp="1xa43telg8kjm">
              Empezar hoy por USD 8,50
            </CTAButton>
          </div>
          <OfferCountdownInline label="Precio especial de lanzamiento por" className="mt-3 justify-center lg:justify-start" data-id="element-103" data-mp="21b66k5ekreqc" />
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        scale: 0.94
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        duration: 0.8,
        delay: 0.15,
        ease: [0.22, 1, 0.36, 1]
      }} data-id="element-104" data-mp="2b2ai0v51khgf">
          <Divider className="mb-2 lg:hidden" data-id="element-105" data-mp="2oye7kb4z01mk" />
          <p className="text-center font-serif text-2xl font-semibold text-rosa-dark sm:text-3xl" data-id="element-106" data-mp="2tztuk8qpki7l">
            Así se verá tu app
          </p>
          <div className="relative mx-auto mt-6 max-w-md" data-id="element-107" data-mp="uoak1pprb1vt">
            <AppCarousel data-id="element-108" data-mp="zkrawrocbucl" />
          </div>
        </motion.div>
      </div>

      <div className="mx-auto max-w-6xl px-4 sm:px-6" data-id="element-109" data-mp="4b45noxezwhc">
        <Divider className="mt-16" data-id="element-110" data-mp="49t5s073b8vi" />

        <motion.div initial={{
        opacity: 0,
        y: 24
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.7,
        delay: 0.1,
        ease: [0.22, 1, 0.36, 1]
      }} className="mt-6 text-center" data-id="element-111" data-mp="3vhp1rn2btul8">
          <h1 className="font-serif text-4xl font-semibold leading-[1.1] text-tinta sm:text-5xl lg:text-6xl" data-id="element-112" data-mp="3hr22ntsnm2om">
            Deja de despertar agotada.
            <span className="block text-rosa-dark" data-id="element-113" data-mp="3rg3mxw68f68e">
              Reconecta con la mujer con energía que eres.
            </span>
          </h1>
          <ul className="mx-auto mt-8 grid max-w-2xl gap-x-8 gap-y-2.5 text-left text-base text-tinta/80 sm:grid-cols-2" data-id="element-114" data-mp="3molkf3ok8xlb">
            {heroBenefits.map(benefit => <li key={benefit} className="flex items-center gap-2.5" data-id="element-115" data-mp="2u17w1tyxhact">
                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-rosa-light/45" aria-hidden="true" data-id="element-116" data-mp="2tyvbra5p6nwl">
                  <CheckIcon className="h-3.5 w-3.5 text-rosa-dark" strokeWidth={2.6} data-id="element-117" data-mp="1ebgpav0dw9ec" />
                </span>
                {benefit}
              </li>)}
          </ul>
          <p className="mx-auto mt-6 max-w-2xl text-lg leading-relaxed text-tinta/75" data-id="element-118" data-mp="1s8n1u7uilbv4">
            Un método suave, día por día, para recuperar tu energía, mejorar tus
            hábitos y reconectar con tu bienestar. Todo desde tu celular, a tu
            ritmo y en la palma de tu mano.
          </p>
          <div className="mt-9 flex justify-center" data-id="element-119" data-mp="1n9ahpuv12iw8">
            <CTAButton onClick={onCTA} icon={SmartphoneIcon} size="lg" data-id="element-120" data-mp="1nc507guhdtn0">
              Empezar hoy por USD 8,50
            </CTAButton>
          </div>
          <OfferCountdownInline className="mt-4 justify-center" data-id="element-121" data-mp="bvyb5c7vvm6d" />
          <p className="glow-pulse mt-6 flex flex-col items-center gap-1 text-base font-semibold text-rosa-dark sm:text-lg" data-id="element-122" data-mp="2ehke36quoha0">
            <span className="inline-flex items-center gap-2.5" data-id="element-123" data-mp="29i7k821ka9xx">
              <FlowerIcon className="h-5 w-5 shrink-0" strokeWidth={1.8} data-id="element-124" data-mp="24rr5mem08i9z" />
              Acceso inmediato · Un solo pago
            </span>
            <span data-id="element-125" data-mp="1zrmn6j1jddh4">Empieza hoy mismo</span>
          </p>
        </motion.div>
      </div>
    </section>;
}